import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, Fragment as _Fragment, withScopeId as _withScopeId, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"
import _imports_0 from './left-arrow.svg'


_pushScopeId("data-v-47b4c236")
const _hoisted_1 = {
  key: 0,
  class: "col-span-3"
}
const _hoisted_2 = { class: "col-span-2 text-[20px] font-semibold leading-[26px] text-center" }
const _hoisted_3 = { class: "flex" }
const _hoisted_4 = { class: "justify-center w-full" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 2 }
const _hoisted_8 = { key: 3 }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("div", { class: "col-span-2 text-center text-[12px] font-[500] leading-[15px] text-pink-third" }, " Aurora Mainnet ", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("div", { class: "col-span-2 my-[24px] create-pool-border" }, null, -1)
const _hoisted_11 = { class: "relative overflowed" }
const _hoisted_12 = {
  key: 0,
  class: ""
}
const _hoisted_13 = {
  key: 1,
  class: ""
}
_popScopeId()

import anime from 'animejs';
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import BalHorizontalSteps from '@/components/_global/BalHorizontalSteps/BalHorizontalSteps.vue';
import AnimatePresence from '@/components/animate/AnimatePresence.vue';
import ChooseWeights from '@/components/cards/CreatePool/ChooseWeights.vue';
import InitialLiquidity from '@/components/cards/CreatePool/InitialLiquidity.vue';
import PoolFees from '@/components/cards/CreatePool/PoolFees.vue';
import PoolSummary from '@/components/cards/CreatePool/PoolSummary.vue';
import PreviewPool from '@/components/cards/CreatePool/PreviewPool.vue';
import SimilarPools from '@/components/cards/CreatePool/SimilarPools.vue';
import SimilarPoolsCompact from '@/components/cards/CreatePool/SimilarPoolsCompact.vue';
import TokenPrices from '@/components/cards/CreatePool/TokenPrices.vue';
import Col3Layout from '@/components/layouts/Col3Layout.vue';
import UnknownTokenPriceModal from '@/components/modals/UnknownTokenPrice/UnknownTokenPriceModal.vue';
import usePoolCreation, {
  POOL_CREATION_STATE_KEY,
  POOL_CREATION_STATE_VERSION,
} from '@/composables/pools/usePoolCreation';
import useAlerts from '@/composables/useAlerts';
import useApp from '@/composables/useApp';
import useBreakpoints from '@/composables/useBreakpoints';
import useTokens from '@/composables/useTokens';
import { lsGet } from '@/lib/utils';
import useWeb3 from '@/services/web3/useWeb3';
import { StepState } from '@/types';

/**
 * STATE
 */

export default _defineComponent({
  setup(__props) {

const accordionWrapper = ref<HTMLElement>();
const hasCompletedMountAnimation = ref(false);
const prevWrapperHeight = ref(0);
const isUnknownTokenModalVisible = ref(false);
const isRestoring = ref(false);

/**
 * COMPOSABLES
 */
const { appLoading } = useApp();
const {
  activeStep,
  similarPools,
  hasInjectedToken,
  hasRestoredFromSavedState,
  tokensList,
  seedTokens,
  setActiveStep,
  setRestoredState,
  importState,
  totalLiquidity,
  resetPoolCreationState,
  retrievePoolAddress,
  retrievePoolDetails,
} = usePoolCreation();
const { removeAlert } = useAlerts();
const { t } = useI18n();
const { upToLargeBreakpoint, isMobile, isDesktop } = useBreakpoints();
const {
  dynamicDataLoading,
  priceFor,
  getToken,
  injectTokens,
  injectedPrices,
  loading: isLoadingTokens,
} = useTokens();
const route = useRoute();
const { isWalletReady } = useWeb3();

/**
 * LIFECYCLE
 */
onBeforeMount(async () => {
  removeAlert('return-to-pool-creation');
  if (accordionWrapper.value) {
    anime.set(accordionWrapper.value, {
      opacity: 0,
    });
  }
  let previouslySavedState = lsGet(
    POOL_CREATION_STATE_KEY,
    null,
    POOL_CREATION_STATE_VERSION
  );
  if (
    activeStep.value === 0 &&
    previouslySavedState !== null &&
    !poolCreateTx.value
  ) {
    isRestoring.value = true;

    // need to make sure to inject any tokens that were chosen
    previouslySavedState = JSON.parse(previouslySavedState);
    importState(previouslySavedState);
    setRestoredState(true);
    await nextTick();
    setActiveStep(previouslySavedState.activeStep);
    if (previouslySavedState.createPoolTxHash) {
      await retrievePoolAddress(previouslySavedState.createPoolTxHash);
    }
  }
  // make sure to inject any custom tokens we cannot inject
  // after tokens have finished loading as it will attempt to
  // inject 'known' tokens too, as during mount, tokens are still loading
  injectUnknownPoolTokens();
  isRestoring.value = false;
});

/**
 * COMPUTED
 */
const poolCreateTx = computed(() => route.params.tx || '');
const doSimilarPoolsExist = computed(() => similarPools.value.length > 0);
const validTokens = computed(() => tokensList.value.filter(t => t !== ''));

const unknownTokens = computed(() => {
  return validTokens.value.filter(token => {
    return priceFor(token) === 0 || injectedPrices.value[token]?.usd;
  });
});

const hasUnknownToken = computed(() =>
  validTokens.value.some(t => priceFor(t) === 0)
);

const steps = computed(() => [
  {
    tooltip: 'Choose tokens & weights',
    state: getStepState(0),
    label: 1,
  },
  {
    tooltip: 'Set pool fees',
    state: getStepState(1),
    label: 2,
  },
  {
    tooltip: 'Similar pools',
    state: StepState.Warning,
    isVisible: doSimilarPoolsExist.value && activeStep.value === 2,
  },
  {
    tooltip: 'Set initial liquidity',
    state: getStepState(3),
    label: 3,
  },
  {
    tooltip: 'Confirm pool creation',
    state: getStepState(4),
    label: 4,
  },
]);

const initialAnimateProps = computed(() => ({
  opacity: 0,
  translateY: '100px',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
}));

const entryAnimateProps = computed(() => ({
  opacity: 1,
  translateY: hasRestoredFromSavedState.value ? '116px' : '0px',
  position: 'relative',
}));

const exitAnimateProps = computed(() => ({
  opacity: 0,
  translateY: '-100px',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
}));

const isLoading = computed(
  () => appLoading.value || dynamicDataLoading.value || isRestoring.value
);

/**
 * FUNCTIONS
 */
function getStepState(idx: number) {
  if (activeStep.value === idx) {
    return StepState.Active;
  } else {
    if (activeStep.value > idx) {
      return StepState.Completed;
    } else {
      return StepState.Todo;
    }
  }
}

function setWrapperHeight(dimensions?: { width: number; height: number }) {
  // need to transform the accordion as everything is absolutely
  // positioned inside the AnimateHeight component
  if (dimensions?.height) prevWrapperHeight.value = dimensions.height;
  let mobileOffset = 20;

  anime({
    targets: accordionWrapper.value,
    translateY: `${prevWrapperHeight.value + mobileOffset}px`,
    easing: 'spring(0.4, 500, 9, 0)',
    complete: () => {
      if (!hasCompletedMountAnimation.value) {
        anime({
          targets: accordionWrapper.value,
          opacity: 1,
          complete: () => {
            hasCompletedMountAnimation.value = true;
          },
        });
      }
    },
  });
}

function handleNavigate(stepIndex: number) {
  if (hasRestoredFromSavedState.value) {
    setRestoredState(false);
  }
  setActiveStep(stepIndex);
}

function handleReset() {
  resetPoolCreationState();
  setActiveStep(0);
}

function handleUnknownModalClose() {
  isUnknownTokenModalVisible.value = false;
}

function showUnknownTokenModal() {
  isUnknownTokenModalVisible.value = true;
}

function injectUnknownPoolTokens() {
  if (!isLoadingTokens.value) {
    const uninjectedTokens = seedTokens.value
      .filter(seedToken => getToken(seedToken.tokenAddress) === undefined)
      .map(seedToken => seedToken.tokenAddress)
      .filter(token => token !== '');
    injectTokens(uninjectedTokens);
  }
}

/**
 * WATCHERS
 */
watch([hasInjectedToken, totalLiquidity], () => {
  setWrapperHeight();
});

// can handle the behaviour to show the unknown token modal
// on next step here, rather than having to clutter the
// usePoolCreation composable further
watch(activeStep, () => {
  if (hasUnknownToken.value && !hasRestoredFromSavedState.value) {
    showUnknownTokenModal();
  }
});

// make sure to inject any custom tokens we cannot inject
// after tokens have finished loading as it will attempt to
// inject 'known' tokens too, as during mount, tokens are still loading
watch(isLoadingTokens, () => {
  injectUnknownPoolTokens();
});

// we need to wait for a ready wallet before executing this
// as we need that getProvider() call to suceed
watch(
  isWalletReady,
  async () => {
    if (isWalletReady.value && poolCreateTx.value) {
      await retrievePoolDetails(poolCreateTx.value as string);
    }
  },
  {
    immediate: true,
  }
);

return (_ctx: any,_cache: any) => {
  const _component_BalStack = _resolveComponent("BalStack")!
  const _component_BalAlert = _resolveComponent("BalAlert")!
  const _component_BalLoadingBlock = _resolveComponent("BalLoadingBlock")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!_unref(upToLargeBreakpoint))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (!_unref(appLoading))
            ? (_openBlock(), _createBlock(_component_BalStack, {
                key: 0,
                vertical: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(BalHorizontalSteps, {
                    title: "Create a weighted pool steps",
                    steps: _unref(steps),
                    onNavigate: handleNavigate
                  }, null, 8, ["steps"]),
                  _createVNode(AnimatePresence, {
                    isVisible: 
          _unref(doSimilarPoolsExist) && _unref(activeStep) === 0 && _unref(validTokens).length
        
                  }, {
                    default: _withCtx(() => [
                      _createVNode(SimilarPoolsCompact)
                    ]),
                    _: 1
                  }, 8, ["isVisible"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass([{
      'container grid grid-cols-2 grid-flow-row auto-rows-auto gap-x-4':
        _unref(isDesktop),
      mobileContainer: _unref(isMobile),
    }, "bg mx-auto max-w-[914px] bg-[#2E2433] rounded-[20px] p-[24px]"])
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          (_unref(activeStep) > 0)
            ? (_openBlock(), _createElementBlock("img", {
                key: 0,
                class: "justify-start",
                src: _imports_0,
                onClick: _cache[0] || (_cache[0] = ($event: any) => (activeStep.value = _unref(activeStep) - 1))
              }))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_4, [
            (!_unref(appLoading) && _unref(activeStep) === 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_5, "Choose tokens & weights"))
              : _createCommentVNode("", true),
            (!_unref(appLoading) && _unref(activeStep) === 1)
              ? (_openBlock(), _createElementBlock("span", _hoisted_6, "Set pool fees"))
              : _createCommentVNode("", true),
            (!_unref(appLoading) && _unref(activeStep) === 2)
              ? (_openBlock(), _createElementBlock("span", _hoisted_7, "Set initial liquidity"))
              : _createCommentVNode("", true),
            (!_unref(appLoading) && _unref(activeStep) === 3)
              ? (_openBlock(), _createElementBlock("span", _hoisted_8, "Confirm pool creation"))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _hoisted_9,
      _hoisted_10,
      _createElementVNode("div", _hoisted_11, [
        _createVNode(AnimatePresence, {
          isVisible: _unref(hasRestoredFromSavedState) && !_unref(appLoading),
          unmountInstantly: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_BalAlert, {
              type: "warning",
              class: "mb-4",
              title: _ctx.$t('createAPool.recoveredState')
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('createAPool.recoveredStateInfo')) + " ", 1),
                _createElementVNode("button", {
                  class: "font-semibold text-blue-500",
                  onClick: handleReset
                }, _toDisplayString(_ctx.$t('clickHere')), 1)
              ]),
              _: 1
            }, 8, ["title"])
          ]),
          _: 1
        }, 8, ["isVisible"]),
        _createVNode(AnimatePresence, {
          isVisible: _unref(isLoading),
          unmountInstantly: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_BalLoadingBlock, { class: "h-64" })
          ]),
          _: 1
        }, 8, ["isVisible"]),
        (!_unref(appLoading) && _unref(activeStep) === 0 && !_unref(hasRestoredFromSavedState))
          ? (_openBlock(), _createBlock(ChooseWeights, {
              key: 0,
              "onUpdate:height": setWrapperHeight
            }))
          : _createCommentVNode("", true),
        (!_unref(appLoading) && _unref(activeStep) === 1)
          ? (_openBlock(), _createBlock(PoolFees, {
              key: 1,
              "onUpdate:height": setWrapperHeight
            }))
          : _createCommentVNode("", true),
        (!_unref(appLoading) && _unref(activeStep) === 2 && _unref(similarPools).length > 0)
          ? (_openBlock(), _createBlock(SimilarPools, { key: 2 }))
          : _createCommentVNode("", true),
        (!_unref(isLoading) && _unref(activeStep) === 3)
          ? (_openBlock(), _createBlock(InitialLiquidity, {
              key: 3,
              "onUpdate:height": setWrapperHeight
            }))
          : _createCommentVNode("", true),
        (!_unref(appLoading) && _unref(activeStep) === 4 && !_unref(dynamicDataLoading))
          ? (_openBlock(), _createBlock(PreviewPool, { key: 4 }))
          : _createCommentVNode("", true)
      ]),
      (_unref(upToLargeBreakpoint))
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
            _createVNode(TokenPrices),
            _createVNode(PoolSummary)
          ]))
        : _createCommentVNode("", true),
      (!_unref(upToLargeBreakpoint))
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
            (!_unref(appLoading))
              ? (_openBlock(), _createBlock(_component_BalStack, {
                  key: 0,
                  vertical: "",
                  spacing: "base"
                }, {
                  default: _withCtx(() => [
                    _createVNode(TokenPrices, { toggleUnknownPriceModal: showUnknownTokenModal }),
                    _createVNode(PoolSummary)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ], 2),
    _createVNode(UnknownTokenPriceModal, {
      isVisible: isUnknownTokenModalVisible.value,
      unknownTokens: _unref(unknownTokens),
      onClose: handleUnknownModalClose
    }, null, 8, ["isVisible", "unknownTokens"])
  ], 64))
}
}

})